import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { Masquerade, masqueradeToken } from 'go-modules/masquerade/masquerade.service';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { LazyPaymentLoader, lazyPaymentLoaderToken } from 'go-modules/payment-panel/lazy-payment-loader.service';
import { GoTourService, goTourServiceToken } from 'go-modules/go-tour/go-tour.service';
import { TermsService } from '@go-modules/services/terms/terms.service';
import { UserService as NgxUserService } from '@go-modules/services/user/user.service';
import { MatDialog } from '@angular/material/dialog';
import { SkipLinkOption, SkipLinksComponent } from '@go-modules/components/acessibility-skip-links/skip-links.component';
import { TermsOfUseDialogComponent } from '@go-modules/components/dialogs/terms-of-use-dialog/terms-of-use-dialog.component';
import { WelcomeDialogComponent } from '@go-modules/components/dialogs/welcome-dialog/welcome-dialog.component';
import { LICENSE_TRANSACTION_TYPE, PAYMENT_TYPES, PURCHASE_TYPES } from 'go-modules/payment-panel/payment-panel.controller';
import { DashboardModule } from '@go-modules/components/dashboard/dashboard.module';
import { SuperBarModule } from '../super-bar/super-bar.module';
import { PWAInstallPromptModule } from '../pwa-install-prompt/pwa-install-prompt.module';

import { Router, RouterModule, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { LoadingModule } from '@go-modules/components/loading/loading.module';
import { RouterService } from '@go-modules/services/router/router.service';
import { AppDataService, appDataServiceToken } from 'dashboard/services/app-data.service';
import { UnifiedLoginScriptService } from '../../services/unified-login-script.service';
import { clientSettings } from 'go-modules/models/common/client.settings';

@Component({
	selector: 'dashboard-root-view',
	standalone: true,
	imports: [
		RouterModule,
		LoadingModule,
		DashboardModule,
		SuperBarModule,
		PWAInstallPromptModule,
		SkipLinksComponent
	],
	templateUrl: './dashboard-root-view.component.html',
	styleUrls: ['./dashboard-root-view.component.scss']
})
export class DashboardRootViewComponent implements OnInit, OnDestroy {
	public skipLinkOptions: SkipLinkOption[];
	public isRootUser: boolean = false;
	public loading: boolean = false;
	public unifiedLoginApiBaseUrl = `${clientSettings.GoReactV2API}`; // Todo: put the actual API URL
	private key = 'welcome-modal-tour';
	private constraints = {
		isOnboarding: true,
		tourStartDate: dayjs('2023-05-23 15:00:00 MDT').toDate()
	};
	private destroy$ = new Subject<void>();

	constructor (
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(masqueradeToken) private masquerade: Masquerade,
		@Inject(lazyPaymentLoaderToken) private paymentLoader: LazyPaymentLoader,
		@Inject(goTourServiceToken) private goTour: GoTourService,
		@Inject(appDataServiceToken) private appDataService: AppDataService,
		private translate: TranslateService,
		private ngxGoModalService: GoModalService,
		private ngxTermsService: TermsService,
		private matDialog: MatDialog,
		private ngxUserService: NgxUserService,
		private router: Router,
		private unifiedLoginScriptService: UnifiedLoginScriptService,
		private routerService: RouterService
	) {}

	public ngOnInit (): void {
		this.unifiedLoginScriptService.load();

		// TODO - add more skip link options
		this.skipLinkOptions = [
			{
				id: 'folders-view-container',
				linkText: this.translate.instant('skip-link_folder-list')
			},
			{
				id: 'folder-view-container',
				linkText: this.translate.instant('skip-link_activity-list')
			},
			{
				id: 'activity-view-container',
				linkText: this.translate.instant('skip-link_session-list')
			}
		];

		this.isRootUser = this.userService.currentUser.is_root_user;

		// Listen to router events for loading state
		// Only show loading when navigating within dashboard routes (not auth routes)
		this.router.events
			.pipe(takeUntil(this.destroy$))
			.subscribe((event) => {
				if (event instanceof NavigationStart) {
					// Show loading at dashboard-root level only for dashboard child routes
					const isDashboardChildRoute = event.url.includes('/dashboard/') &&
						!event.url.includes('/auth/') &&
						event.url !== '/dashboard' &&
						!event.url.startsWith('/dashboard?');
					this.loading = isDashboardChildRoute;
				} else if (
					event instanceof NavigationEnd ||
					event instanceof NavigationCancel ||
					event instanceof NavigationError
				) {
					this.loading = false;
				}
			});

		// determine student course-credits
		this.ngxUserService.getBillingBalances(this.userService.currentUser.user_id)
			.pipe(takeUntil(this.destroy$))
			.subscribe((response) => {
				this.userService.currentUser.plan = response.plan;
				this.userService.currentUser.totalStudentCredits = response.credits.reduce((total, credit) => {
					return total + Number(credit.units_available);
				}, 0);
			});

		this.goTour.canTourBeViewed(this.key, this.constraints).then(()=> {
			this.openWelcomeModal();
		}).catch(()=> {
			if (!this.masquerade.isMasked()) {
				// Opens terms of service modal
				this.ngxTermsService.requiresUpdate().pipe(takeUntil(this.destroy$)).subscribe({
					next: () => { TermsOfUseDialogComponent.open(this.matDialog); },
					error: () => {} // Nothing to do if no terms to accept
				});
			}
		});
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public openWelcomeModal () {
		const dialogRef = this.ngxGoModalService.open(
			WelcomeDialogComponent,
			false,
			{data: {
				user: this.userService.currentUser,
				account: this.selectedService.getAccount()
			}}
		);
		dialogRef.afterClosed().subscribe((selfPay: boolean) => {
			this.goTour.markTourViewed(this.key);
			if (selfPay) {
				this.paymentLoader.openPayForm(
					this.userService.currentUser,
					this.selectedService.getAccount(),
					() => {},
					null,
					PAYMENT_TYPES.CARD,
					PURCHASE_TYPES.LICENSE,
					LICENSE_TRANSACTION_TYPE.INITIAL
				);
			}
		});
	}
}
